import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import { useItineraryContext } from './ChatTripPlannerPage/contexts/ItineraryContext'
import { useCartButtonSummary } from './ChatTripPlannerPage/hooks/useCartButtonSummary'
import { useFetchCartPanelOffers } from './ChatTripPlannerPage/hooks/useFetchCartPanelOffers'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import CartButton from './ChatTripPlannerPage/components/CartButton'
import DownloadItineraryButton from './ChatTripPlannerPage/components/DownloadItineraryButton'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import ContactOptions from 'components/Common/ContactOptions'
import {
  TripPlannerStickyHeader,
  TripPlannerStyledLogo,
  LeftContent,
  RightContent,
} from 'tripPlanner/components/Header/Shared'
import Clickable from 'components/Common/Clickable'
import NavBarRegionButton from 'components/NavKit/NavBarButtons/NavBarRegionButton'
import { useTripPlannerMenuDrawer } from './ChatTripPlannerPage/hooks/useTripPlannerMenuDrawer'

const COMPACT_HEADER_MEDIA_QUERY = '@media (max-height: 800px)'
const CONTACT_TAGLINE_CLASS_NAME = 'trip-planner-contact-tagline'
const NAVIGATION_HEIGHT = rem(40)

const HeaderLayout = styled(TripPlannerStickyHeader)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: ${rem(40)} minmax(0, 1fr);
  grid-template-areas:
    'back contact'
    'navigation actions';
  height: calc(${rem(40)} + ${NAVIGATION_HEIGHT});
  flex-shrink: 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: linear-gradient(
    to bottom,
    ${props => props.theme.palette.neutral.surface.lightBackground} ${rem(40)},
    ${props => props.theme.palette.neutral.surface.normalBackground} ${rem(40)}
  );

  ${COMPACT_HEADER_MEDIA_QUERY} {
    grid-template-columns: auto minmax(0, 1fr) auto auto;
    grid-template-rows: minmax(0, 1fr);
    grid-template-areas: 'navigation back contact actions';
    column-gap: ${rem(8)};
    height: ${NAVIGATION_HEIGHT};
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  @media print {
    display: none;
  }
`

const BackButton = styled(TextButton)`
  grid-area: back;
  justify-self: start;
`

const HeaderContactOptions = styled(ContactOptions)`
  grid-area: contact;

  ${COMPACT_HEADER_MEDIA_QUERY} {
    .${CONTACT_TAGLINE_CLASS_NAME} {
      display: none;
    }
  }
`

const HeaderNavigation = styled(LeftContent)`
  grid-area: navigation;
`

const HeaderActions = styled(RightContent)`
  grid-area: actions;

  ${COMPACT_HEADER_MEDIA_QUERY} {
    margin-inline-start: 0;
  }
`

const LogoWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`

const HeaderNavBarRegionButton = styled(NavBarRegionButton)`
  border-radius: ${props => props.theme.borderRadius.round};
`

const HeaderDownloadItineraryButton = styled(DownloadItineraryButton)`
  border-radius: ${props => props.theme.borderRadius.round};
`

interface Props {
  chatId: number | undefined
  onStartNewChat: () => void
  onChatSelect: (chatId: number) => void
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  isDevMode: boolean
  isRewindMode: boolean
  onDevModeToggle: () => void
  onRewindModeToggle: () => void
  className?: string
}

function TripPlannerHeader({
  chatId,
  onStartNewChat,
  onChatSelect,
  onChatDeleted,
  isDevMode,
  isRewindMode,
  onDevModeToggle,
  onRewindModeToggle,
  className,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  // ── Own data reads ──

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)
  const eventBus = useTripPlannerEventBus()
  const { itineraryData, hasItineraryData } = useItineraryContext()
  useFetchCartPanelOffers(itineraryData, true)
  const cartSummary = useCartButtonSummary(itineraryData, isLoggedIn)

  const handleCartClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const handleOpenDrawer = useTripPlannerMenuDrawer({
    isLoggedIn,
    isStaff,
    isDevMode,
    isRewindMode,
    onStartNewChat,
    onChatSelect,
    onChatDeleted,
    onDevModeToggle,
    onRewindModeToggle,
    activeChatId: chatId,
    hasActiveChatItinerary: hasItineraryData,
  })

  const handleLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  // ── i18n ──

  const loginButtonText = intl.formatMessage({
    id: 'ARcYTJ2nmHAv35KNSneu',
    defaultMessage: 'Log in to save your trip',
    description: 'Button text prompting unauthenticated users to log in to save their trip plan',
  })

  const backButtonText = intl.formatMessage({
    id: 'uvkQRhGsEtSZkBcb2NHS',
    defaultMessage: 'Back to Luxury Escapes',
    description: 'Navigation button text to return to the main Luxury Escapes site from TripPlanner',
  })

  const openMenuAriaLabel = intl.formatMessage({
    id: 'sIGePGauk8KDHGHEBhex',
    defaultMessage: 'Open menu',
    description: 'Accessible label for the hamburger menu button in the TripPlanner header bar',
  })

  const aiTripPlannerLabel = intl.formatMessage({
    id: 'EYQsWpC_SwW6ZS0Xb0Yz',
    defaultMessage: 'AI TripPlanner',
    description: 'Branding label for the AI TripPlanner feature in the header',
  })
  const showCartButton = !!itineraryData

  const rightContent = useMemo(() => {
    if (isLoggedIn) {
      return (
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {hasItineraryData && <HeaderDownloadItineraryButton chatId={chatId} />}
          <HeaderNavBarRegionButton />
          {showCartButton && <CartButton
            itemCount={cartSummary.bookableItemCount}
            totalPrice={cartSummary.estimatedTotal}
            isLoading={cartSummary.isLoading}
            onClick={handleCartClick}
          />}
        </Group>
      )
    }

    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasItineraryData && <HeaderDownloadItineraryButton chatId={chatId} />}
        <HeaderNavBarRegionButton />
        {showCartButton && <CartButton
          itemCount={cartSummary.bookableItemCount}
          totalPrice={cartSummary.estimatedTotal}
          isLoading={cartSummary.isLoading}
          onClick={handleCartClick}
        />}
        <TextButton
          kind="secondary"
          size="medium"
          onClick={handleLogin}
        >
          {loginButtonText}
        </TextButton>
      </Group>
    )
  }, [isLoggedIn, hasItineraryData, chatId, handleLogin, loginButtonText, showCartButton, cartSummary.bookableItemCount, cartSummary.estimatedTotal, cartSummary.isLoading, handleCartClick])

  return (
    <HeaderLayout className={className}>
      <BackButton
        kind="tertiary"
        size="small"
        startIcon={<LineArrowLeftIcon />}
        to="/"
      >
        {backButtonText}
      </BackButton>
      <HeaderContactOptions taglineClassName={CONTACT_TAGLINE_CLASS_NAME} />
      <HeaderNavigation>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <IconButton
            kind="tertiary"
            size="medium"
            onClick={handleOpenDrawer}
            aria-label={openMenuAriaLabel}
          >
            <LineBarsIcon />
          </IconButton>
          {chatId === undefined && (
            <Clickable to="/">
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <LogoWrapper>
                  <TripPlannerStyledLogo />
                </LogoWrapper>
                <BodyText variant="large" weight="bold">{aiTripPlannerLabel}</BodyText>
              </Group>
            </Clickable>
          )}
        </Group>
      </HeaderNavigation>
      {rightContent && (
        <HeaderActions>
          {rightContent}
        </HeaderActions>
      )}
    </HeaderLayout>
  )
}

export default TripPlannerHeader
